/** The standalone speaking bot — an open-ended voice conversation.
 *
 * The learner picks a language, level and (optionally) a topic, presses
 * Start ONCE, and then just talks: the bot greets aloud, the mic arms
 * itself, and the two take turns continuously until the learner ends it.
 * Nothing auto-stops it mid-practice. On close it gives friendly feedback.
 *
 * The loop itself is the shared useVoiceConversation hook (same one the
 * exam speaking partner uses); this page owns only the setup screen.
 */
import { useEffect, useState } from "react";
import * as api from "../../api/exams";
import { AskDoubt } from "../../components/AskDoubt";
import { ContentLocked } from "../../components/ContentLocked";
import { ApiError } from "../../api/http";
import { Badge, Button, Card, Spinner } from "../../components/ui";
import { Link } from "react-router-dom";
import { useT } from "../../i18n";
import { sttAvailable } from "../../utils/stt";
import { SpeakingBriefing } from "./SpeakingBriefing";
import { VoiceOrb } from "./VoiceOrb";
import { PartnerLine } from "./PartnerLine";
import { VoicePicker } from "./VoicePicker";
import { FeedbackView, Transcript } from "./SpeakingPartner";
import { useVoiceConversation } from "./useVoiceConversation";

const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];
/** Speech-recognition locales for the supported chat languages. */
const LOCALES: Record<string, string> = {
  de: "de-DE", en: "en-US", fr: "fr-FR", es: "es-ES", it: "it-IT",
};

/** "12 Mar 2026 · Reisen · B1" — whatever of that the row actually has. */
function sessionLabel(row: api.FreeChatSession): string {
  const when = row.created_at
    ? new Date(row.created_at).toLocaleDateString(undefined, {
        day: "numeric", month: "short", year: "numeric",
      })
    : "";
  return [when, row.topic, row.level].filter(Boolean).join(" · ");
}

export function SpeakingBotPage() {
  const t = useT();
  const [status, setStatus] = useState<api.FreeChatStatus | null>(null);
  const [language, setLanguage] = useState("de");
  // "auto" = adapt to the learner's latest read (sent as null); otherwise an
  // explicit CEFR level.
  const [level, setLevel] = useState("auto");
  const [topic, setTopic] = useState("");
  const [input, setInput] = useState("");
  const [past, setPast] = useState<api.FreeChatSession[]>([]);
  const [reading, setReading] = useState<api.FreeChatSession | null>(null);
  const [restoring, setRestoring] = useState(true);
  // WHY the partner is unavailable, kept apart from WHETHER it is. Folding
  // every failure into `configured: false` told a learner whose plan simply
  // does not include the speaking partner that the server had never been
  // set up - so they asked an administrator to fix something that was not
  // broken, instead of being shown what to buy.
  const [blocked, setBlocked] = useState<ApiError | Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .freeSpeakingStatus()
      .then((s) => {
        if (!cancelled) setStatus(s);
      })
      .catch((err) => {
        if (cancelled) return;
        setBlocked(err instanceof Error ? err : new Error("failed"));
        setStatus({ configured: true, quota: null, languages: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const chat = useVoiceConversation({
    startSession: async () => {
      const r = await api.startFreeSpeaking({
        language,
        level: level === "auto" ? null : level,
        topic: topic.trim() || undefined,
        // The partner the server resolved for this learner, sent
        // EXPLICITLY and then resent on every turn. Leaving it out would
        // let the server re-resolve each turn, so changing the preference
        // mid-conversation would swap personality halfway through a chat -
        // which the old in-place picker deliberately prevented by locking
        // itself once a session began.
        model: status?.active_model ?? null,
      });
      setStatus((s) => (s ? { ...s, quota: r.quota } : s));
      return r;
    },
    lang: LOCALES[language] ?? "de-DE",
    // How patient the microphone is comes from the level: "auto" means
    // whatever the learner's reading suggested, else their own pick.
    level: level === "auto" ? status?.suggested_level ?? null : level,
    model: status?.active_model ?? null,
    onFinished: () => void loadHistory(),
  });
  const {
    session, messages, turns, busy, listening, speaking, error, ended, paused, heard,
    transcribing, missed, level: micLevel, canFinish,
  } = chat;

  const loadHistory = () =>
    api
      .freeSpeakingHistory()
      .then((r) => setPast(r.sessions))
      .catch(() => setPast([]));

  // A conversation used to live only in this tab's memory, so a refresh or a
  // stray back button lost it with no way back in. Ask the server whether one
  // is still open and step straight back into it.
  useEffect(() => {
    let cancelled = false;
    api
      .activeFreeSpeaking()
      .then((r) => {
        if (cancelled || !r.session) return;
        setLanguage(r.session.language ?? "de");
        chat.resume(r.session);
      })
      .catch(() => undefined)   // no open chat is the normal case, not an error
      .finally(() => {
        if (!cancelled) setRestoring(false);
      });
    void loadHistory();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Closing the tab mid-conversation is recoverable now, but it should still
  // take a deliberate confirmation rather than one stray keystroke.
  useEffect(() => {
    if (!session || ended) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [session, ended]);

  const onSendTyped = () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    chat.sendText(text);
  };

  if (!status || restoring) {
    return (
      <div className="grid place-items-center py-12">
        <Spinner size={28} />
      </div>
    );
  }

  const header = (
    <div>
      <h1 className="text-3xl font-extrabold">{t("bot.title")}</h1>
      <p className="mt-1 text-muted">{t("bot.subtitle")}</p>
    </div>
  );

  // A plan that does not include the speaking partner is an upsell, not a
  // fault: name it, and point at the thing that opens it.
  if (blocked instanceof ApiError && blocked.status === 403) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        {header}
        <ContentLocked module={blocked.requiredModule} />
      </div>
    );
  }

  if (blocked) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        {header}
        <Card>
          <p role="alert" className="text-sm text-muted">
            {t("common.error.generic")}
          </p>
        </Card>
      </div>
    );
  }

  // The one case the old message was actually right about: this deployment
  // genuinely has no AI key.
  if (!status.configured) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        {header}
        <Card>
          <p role="status" className="text-sm text-muted">{t("speaking.notConfigured")}</p>
        </Card>
      </div>
    );
  }

  if (reading) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        {header}
        <Card>
          <Button variant="ghost" onClick={() => setReading(null)}>
            ← {t("bot.backToStart")}
          </Button>
          <p className="mt-2 text-sm text-muted">{sessionLabel(reading)}</p>
          {/* FeedbackView already renders the transcript alongside the
              review; a session that ended too short to grade has only the
              transcript to show. */}
          <div className="mt-3">
            {reading.feedback ? (
              <FeedbackView session={reading} onAgain={() => setReading(null)} />
            ) : (
              <Transcript turns={reading.transcript} />
            )}
          </div>
        </Card>
      </div>
    );
  }

  if (ended) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        {header}
        <Card>
          <FeedbackView session={ended} onAgain={chat.reset} />
        </Card>
      </div>
    );
  }

  // Setup screen — pick language, level, optional topic.
  if (!session) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        {header}
        <Card>
          <p className="text-5xl" aria-hidden>🗣️</p>
          <p className="mt-3 text-sm text-ink">{t("bot.intro")}</p>

          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="bot-language" className="text-sm font-semibold text-ink">
                {t("bot.language")}
              </label>
              <select
                id="bot-language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="mt-1 w-full rounded-control border border-line bg-surface px-3 py-2 text-sm text-ink"
              >
                {status.languages.map((l) => (
                  <option key={l.code} value={l.code}>{l.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="bot-level" className="text-sm font-semibold text-ink">
                {t("bot.level")}
              </label>
              <select
                id="bot-level"
                value={level}
                onChange={(e) => setLevel(e.target.value)}
                className="mt-1 w-full rounded-control border border-line bg-surface px-3 py-2 text-sm text-ink"
              >
                <option value="auto">{t("bot.levelAuto")}</option>
                {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
              {level === "auto" && (
                <p className="mt-1 text-xs text-muted">
                  {status.suggested_level
                    ? t("bot.levelAutoAt", { level: status.suggested_level })
                    : t("bot.levelAutoHint")}
                </p>
              )}
            </div>
          </div>

          <div className="mt-4">
            <label htmlFor="bot-topic" className="text-sm font-semibold text-ink">
              {t("bot.topic")}
            </label>{" "}
            <span className="text-xs text-muted">{t("bot.topicHint")}</span>
            <input
              id="bot-topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={t("bot.topicPlaceholder")}
              className="mt-1 w-full rounded-control border border-line bg-surface px-3 py-2 text-sm text-ink"
            />
          </div>

          {/* Who you are practising with, and what a conversation costs.
              Changed in the profile, not here: it is a preference, and
              re-deciding it before every chat is a toll on the one screen
              a learner opens to practise. */}
          <PartnerLine
            models={status.models ?? []}
            activeId={status.active_model ?? null}
          />

          {/* The other way to practise speaking: a real spoken exchange
              rather than this type-or-dictate one. Linked rather than
              merged because it is billed by the minute from a different
              pot, and mixing the two on one screen would hide that. */}
          <Link
            to="/speak/live"
            className="mt-3 inline-block text-sm font-semibold text-brand-dark underline underline-offset-2"
          >
            {t("live.title")}
          </Link>

          <VoicePicker lang={LOCALES[language] ?? "de-DE"} />

          {!sttAvailable() && (
            <p className="mt-4 rounded-control bg-canvas p-3 text-xs text-muted">
              {t("speaking.micUnavailable")}
            </p>
          )}
          {error && <p role="alert" className="mt-3 text-sm text-danger">{error}</p>}

          {/* free mode: no exam preparation note, there is no exam here. */}
          <div className="mt-4">
            <SpeakingBriefing mode="free" />
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-muted">
              {status.quota && t("speaking.quotaUsed", {
                used: status.quota.used, limit: status.quota.limit,
              })}
            </span>
            <Button onClick={() => void chat.start()} loading={busy}>
              🎤 {t("bot.start")}
            </Button>
          </div>
        </Card>

        {/* Past conversations. The transcript was always written to the
            session row and then unreachable — a learner could never re-read
            what they said or the feedback they were given. */}
        {past.length > 0 && (
          <Card title={t("bot.past")}>
            <ul className="divide-y divide-line">
              {past.map((row) => (
                <li key={row.id}>
                  <button
                    onClick={() => setReading(row)}
                    className="flex w-full items-center justify-between gap-3 py-3 text-left hover:text-brand"
                  >
                    <span className="text-sm text-ink">{sessionLabel(row)}</span>
                    <span className="shrink-0 text-xs text-muted">
                      {row.score_percent !== null ? `${row.score_percent}%` : "—"}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        )}
        <AskDoubt />
      </div>
    );
  }

  // Live conversation — runs until the learner ends it.
  return (
    <div className="mx-auto max-w-2xl space-y-4">
      {header}
      <Card>
        {/* The client called the old screen "very boring" — there was
            nothing to look at while talking. The orb says which turn it is
            without the learner reading anything. */}
        <VoiceOrb
          state={
            busy || transcribing ? "thinking"
              : speaking ? "speaking"
                : listening ? "listening"
                  : "idle"
          }
          level={micLevel}
        />

        {/* How this works, said once, in the learner's own UI language.
            A beginner told only "Hallo, wie geht es dir?" does not know
            whether to speak, type, how long to take, or what happens if
            they get stuck. */}
        {turns === 0 && !paused && (
          <div className="mb-3 rounded-control bg-canvas p-3 text-sm text-ink">
            <p className="font-semibold">{t("bot.howTitle")}</p>
            <ul className="mt-1 list-disc space-y-1 pl-5 text-muted">
              <li>{t("bot.howSpeak")}</li>
              <li>{t("bot.howPause")}</li>
              <li>{t("bot.howStuck")}</li>
              <li>{t("bot.howEnd")}</li>
            </ul>
          </div>
        )}

        <Transcript turns={messages} />

        {/* The gap between finishing a sentence and the partner answering
            used to be silence with nothing on screen, which reads as a
            dead microphone. Name what is happening. */}
        {transcribing && (
          <p className="mt-2 text-sm italic text-muted" aria-live="polite">
            {t("speaking.transcribing")}
          </p>
        )}
        {missed && (
          <p role="status" className="mt-2 text-sm font-semibold text-warning">
            {t("speaking.missed")}
          </p>
        )}

        {/* Being heard is invisible otherwise, and a learner who cannot
            tell whether the microphone is working stops talking. */}
        {heard && (
          <p className="mt-2 text-sm italic text-muted" aria-live="polite">
            {heard}
          </p>
        )}

        {paused && (
          <div className="mt-4 rounded-control bg-canvas p-3">
            <p className="text-sm text-ink">{t("bot.resumed")}</p>
            <Button className="mt-2" onClick={chat.listen}>
              🎤 {t("bot.continue")}
            </Button>
          </div>
        )}

        <div className="mt-4 flex items-center gap-3 text-sm text-muted" data-testid="bot-status">
          {busy && (
            <span className="inline-flex items-center gap-2">
              <Spinner size={14} /> {t("speaking.thinking")}
            </span>
          )}
          {!busy && listening && <span>🎙️ {t("speaking.listening")}</span>}
          {/* The silence timer is generous so a pause is not an ending;
              a learner who IS finished should not have to wait it out. */}
          {!busy && listening && canFinish && (
            <Button variant="secondary" onClick={chat.finishTurn}>
              ✓ {t("speaking.done")}
            </Button>
          )}
          {!busy && !listening && !paused && !transcribing && (
            <span>{t("bot.partnerSpeaking")}</span>
          )}
          <span className="ml-auto">{turns} {t("speaking.turnsWord")}</span>
        </div>

        {error && <p role="alert" className="mt-2 text-sm text-danger">{error}</p>}

        <div className="mt-4 flex gap-2">
          <input
            aria-label={t("speaking.typeFallback")}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSendTyped();
            }}
            className="w-full min-w-0 rounded-control border border-line bg-surface px-3 py-2 text-sm text-ink"
            placeholder={t("speaking.typeFallback")}
          />
          <Button variant="secondary" onClick={onSendTyped} disabled={busy || !input.trim()}>
            {t("speaking.send")}
          </Button>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <Badge tone="brand">{t("speaking.aiPartner")}</Badge>
          <Button variant="danger" onClick={() => void chat.end()} loading={busy}>
            {t("bot.end")}
          </Button>
        </div>
      </Card>
    </div>
  );
}
